import React from 'react'
import Navbar from '../components/sections/Navbar'
import Footer from '../components/sections/Footer'
import LightRays from '../components/ui/LightRays'
import { useScrollAnimation } from '../hooks/useScrollAnimation'
import { useSEO } from '../hooks/useSEO'
import balaPhoto from '../assets/Bala.jpg'
import karthikPhoto from '../assets/Karthik.jpg'
import akshayPhoto from '../assets/Akshay.png'
import priyankaPhoto from '../assets/Priyanka.jpg'
import sumantPhoto from '../assets/Sumanth.jpg'

const CEO_PHOTO = balaPhoto

const TEAM_MEMBERS = [
  {
    id: 'priyanka',
    name: 'Priyanka A',
    role: 'Director, AI Architect',
    initials: 'PA',
    photo: priyankaPhoto,
    linkedin: 'https://www.linkedin.com/in/priyanka-mark/',
  },
  {
    id: 'akshay',
    name: 'Akshay Reddy Emmadi',
    role: 'Senior Product Engineer',
    initials: 'AE',
    photo: akshayPhoto,
    linkedin: 'https://www.linkedin.com/in/akshay-reddy-emmadi/',
  },
  {
    id: 'sumant',
    name: 'Sumant Sankurwar',
    role: 'Senior Product Engineer',
    initials: 'SS',
    photo: sumantPhoto,
    linkedin: 'https://www.linkedin.com/in/sumant-sankurwar-06974a246/',
  },
  {
    id: 'karthik',
    name: 'Karthik Santhalingam',
    role: 'Associate UX Designer',
    initials: 'KS',
    photo: karthikPhoto,
    linkedin: 'https://www.linkedin.com/in/karthikux/',
  },
]

const aboutSchema = [
  {
    '@type': 'AboutPage',
    '@id': 'https://augmentappz.ai/about',
    url: 'https://augmentappz.ai/about',
    name: 'About AugmentAppz - Enterprise AI Leadership',
    description: 'Learn about AugmentAppz, our mission to pioneer AI-native enterprise solutions, our leadership, and the values that drive us.',
    publisher: { '@type': 'Organization', name: 'AugmentAppz', url: 'https://augmentappz.ai/' },
  },
  {
    '@type': 'Person',
    name: 'Balavigneshwaran M',
    jobTitle: 'Founder & CEO',
    worksFor: { '@type': 'Organization', name: 'AugmentAppz', url: 'https://augmentappz.ai/' },
    description: 'Enterprise architect with over 16 years of experience in IT Automation, Platform Engineering, and AI-driven solutions.',
    sameAs: ['https://www.linkedin.com/in/balavigneshwaran-manogaran', 'https://x.com/AugmentWithBala'],
  },
]

function VisionIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M2 12C3.6 7.6 7.5 4.5 12 4.5S20.4 7.6 22 12c-1.6 4.4-5.5 7.5-10 7.5S3.6 16.4 2 12z"
        stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="3.5" stroke="#7C3AED" strokeWidth="1.5"/>
      <circle cx="12" cy="12" r="1.3" fill="#7C3AED" opacity="0.85"/>
      <circle cx="13.6" cy="10.4" r="0.6" fill="#7C3AED" opacity="0.45"/>
    </svg>
  )
}

function MissionIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"
        stroke="#06B6D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"
        stroke="#06B6D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"
        stroke="#06B6D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"
        stroke="#06B6D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function LinkedInIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="2" y="9" width="4" height="12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="4" cy="4" r="2" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  )
}

function BadgeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Shield */}
      <path d="M12 2L4 5.5V11c0 4.5 3.4 8.7 8 9.9 4.6-1.2 8-5.4 8-9.9V5.5L12 2z"
        fill="rgba(124,58,237,0.12)" stroke="#7C3AED" strokeWidth="1.5" strokeLinejoin="round"/>
      {/* Checkmark */}
      <path d="M8.5 12l2.5 2.5 5-5" stroke="#7C3AED" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

export default function AboutPage() {
  useSEO({
    title: 'About Us - AugmentAppz | Enterprise AI Leadership',
    description: 'AugmentAppz.ai is pioneering AI-native enterprise solutions. Meet our leadership, learn our values, and discover how we help organizations build intelligent systems at scale.',
    keywords: 'about AugmentAppz, enterprise AI leadership, AI company Bengaluru, Balavigneshwaran M, AI-native organization, AI transformation team',
    canonical: 'https://augmentappz.ai/about',
    jsonLd: aboutSchema,
  })

  useScrollAnimation()

  return (
    <>
      <Navbar />
      <main aria-label="About Us page">

        {/* Hero */}
        <section className="about-hero" aria-label="About Us">
          <LightRays
            raysOrigin="top-center"
            raysColor="#7c3aed"
            raysSpeed={0.8}
            lightSpread={1.2}
            rayLength={1.5}
            pulsating={true}
            fadeDistance={0.8}
            saturation={0.9}
            mouseInfluence={0.08}
          />
          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div className="about-hero-content" data-animate="fade-up">
              <div className="about-hero-accent" aria-hidden="true" />
              <h1 className="about-hero-title">About Us</h1>
              <p className="about-hero-sub">
                AugmentAppz.ai is pioneering the next generation of enterprise AI solutions, creating intelligent systems that augment human potential and transform business capabilities.
              </p>
            </div>
          </div>
        </section>

        {/* Leadership */}
        <section className="section about-leadership" aria-label="Our Team">
          <div className="container">
            <div className="section-header" data-animate="fade-up">
              <h2 className="section-heading">Meet our team</h2>
            </div>

            {/* CEO profile card */}
            <div data-animate="fade-up" data-delay="80">
              <div className="ceo-profile-card">

                {/* Photo area */}
                <div className="ceo-profile-photo-wrap">
                  {CEO_PHOTO
                    ? <img src={CEO_PHOTO} alt="Balavigneshwaran M" className="ceo-profile-img" />
                    : (
                      <div className="ceo-profile-placeholder" aria-hidden="true">
                        <span className="ceo-profile-initials">BM</span>
                      </div>
                    )
                  }
                  <div className="ceo-profile-photo-fade" aria-hidden="true" />
                </div>

                {/* Content */}
                <div className="ceo-profile-body">
                  <span className="ceo-role-badge">Founder / CEO</span>
                  <h3 className="ceo-profile-name">Balavigneshwaran M</h3>
                  <p className="ceo-tagline">Ex-Enterprise Architect building the future of AI-native platforms</p>

                  <div className="ceo-stats-grid">
                    <div className="ceo-stat-card">
                      <p className="ceo-stat-card__label">Deep Tech Expertise</p>
                      <p className="ceo-stat-card__value">16+ years in AI, automation &amp; platform engineering</p>
                    </div>
                    <div className="ceo-stat-card">
                      <p className="ceo-stat-card__label">Strategic Impact</p>
                      <p className="ceo-stat-card__value">Spearheaded $1B+ revenue-impacting programs via IP-led differentiation</p>
                    </div>
                    <div className="ceo-stat-card">
                      <p className="ceo-stat-card__label">Thought Leadership</p>
                      <p className="ceo-stat-card__value">Applied AI Curriculum Author; mentored 500+ professionals</p>
                    </div>
                    <div className="ceo-stat-card">
                      <p className="ceo-stat-card__label">Innovation Portfolio</p>
                      <p className="ceo-stat-card__value">6+ IPs, 1 active patent filed in AI-native pipelines</p>
                    </div>
                  </div>

                  <div className="ceo-profile-footer">
                    <a
                      href="https://www.linkedin.com/in/balavigneshwaran-manogaran/"
                      className="ceo-connect-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Connect on LinkedIn"
                    >
                      <LinkedInIcon />
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>

              </div>
            </div>

            {/* Team members grid */}
            <div className="team-members-grid" data-animate="fade-up" data-delay="160">
              {TEAM_MEMBERS.map(member => (
                <a
                  key={member.id}
                  className="team-member-card"
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${member.name} - ${member.role}`}
                >
                  {/* Photo */}
                  <div className="tmc-photo-wrap">
                    {member.photo
                      ? <img src={member.photo} alt={member.name} className="tmc-photo-img" />
                      : (
                        <div className="tmc-photo-placeholder" aria-hidden="true">
                          <span className="tmc-initials">{member.initials}</span>
                        </div>
                      )
                    }
                    <div className="tmc-photo-fade" aria-hidden="true" />
                  </div>

                  {/* Info below photo */}
                  <div className="tmc-body">
                    <div className="tmc-name-row">
                      <div className="tmc-name-col">
                        <p className="tmc-name">{member.name}</p>
                        <p className="tmc-role">{member.role}</p>
                      </div>
                      <span className="tmc-li-icon" aria-hidden="true">
                        <LinkedInIcon />
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>

          </div>
        </section>

        {/* What Drives Our Direction */}
        <section className="section about-mv" aria-label="What drives our direction">
          <div className="drives-card">

            <div className="drives-card-header" data-animate="fade-up">
              <h2 className="drives-heading">What drives our direction</h2>
            </div>

            <div className="drives-section-divider" data-animate="fade-up" data-delay="150" aria-hidden="true" />

            <div className="drives-two-col">

              <div className="drives-entry" data-animate="slide-left" data-delay="250">
                <div className="drives-icon-row">
                  <VisionIcon />
                  <span className="drives-col-label">Vision</span>
                </div>
                <p className="drives-col-desc">
                  To be the global standard platform for purpose-built, AI-native organizations.
                </p>
                <p className="drives-col-sub">
                  AugmentAppz envisions a future where enterprises are built around intelligent
                  systems that continuously learn, automate processes, and drive innovation at scale.
                </p>
              </div>

              <div className="drives-entry" data-animate="slide-right" data-delay="400">
                <div className="drives-icon-row">
                  <MissionIcon />
                  <span className="drives-col-label">Mission</span>
                </div>
                <p className="drives-col-desc">
                  Enable businesses of every size to build and deploy production-grade,
                  AI-native applications.
                </p>
                <p className="drives-col-sub">
                  AugmentAppz is focused on simplifying the journey toward AI-native architectures
                  by providing the platform and expertise required to design, deploy, and scale
                  intelligent systems.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Team statement */}
        <section className="section about-statement" aria-label="Team statement">
          <div className="container">
            <div className="about-statement-inner" data-animate="fade-up">
              <h2 className="about-statement-heading">
                <span className="about-statement-line">AI-native thinking.</span>
                <span className="about-statement-line">Enterprise-grade execution.</span>
                <span className="about-statement-line">Transformation at scale.</span>
              </h2>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  )
}